<template>
    <div class="sf-wizard" :class="'sf-wizard--' + screen">
        <transition name="sf-screen-fade" mode="out-in">
            <div
                v-if="screen === 'intro'"
                key="intro"
                class="sf-wizard__view sf-wizard__view--intro"
            >
                <div class="container">
                    <persona-intro
                        :persona="personaData"
                        :selector-url="selectorUrl"
                        @start="startConversation"
                    />
                </div>
            </div>

            <div
                v-else-if="screen === 'chat'"
                key="chat"
                class="sf-wizard__view sf-wizard__view--chat"
            >
                <persona-header :persona="personaData"/>
                <div class="container sf-wizard__chat-container">
                    <chat-stream
                        :messages="messages"
                        :is-loading="isLoading"
                        :state="state"
                        @answer="handleAnswer"
                    />
                </div>
            </div>

            <div
                v-else-if="screen === 'loading'"
                key="loading"
                class="container sf-wizard__view sf-wizard__view--loading"
            >
                <loading-screen
                    :persona="personaData"
                />
            </div>

            <div
                v-else-if="screen === 'result'"
                key="result"
                class="sf-wizard__view sf-wizard__view--result"
            >
                <storyfinder-result
                    :persona="personaData"
                    :advice-text="adviceText"
                    :story-text="storyText"
                    :groups="groups"
                    @restart="reset"
                />
            </div>
        </transition>
    </div>
</template>

<script>
import PersonaIntro from './PersonaIntro.vue';
import PersonaHeader from './PersonaHeader.vue';
import ChatStream from './ChatStream.vue';
import LoadingScreen from './LoadingScreen.vue';
import StoryfinderResult from './StoryfinderResult.vue';
import {loadResult, saveResult, clearResult} from './resultStorage';

export default {
    name: 'StoryfinderWizard',

    components: {
        PersonaIntro,
        PersonaHeader,
        ChatStream,
        LoadingScreen,
        StoryfinderResult,
    },

    props: {
        personaKey: {type: String, default: ''},
        selectorUrl: {type: String, default: '/verhaalvinder'},
        endpointNext: {type: String, required: true},
        endpointAdvice: {type: String, required: true},
        csrfToken: {type: String, required: true},
    },

    data() {
        return {
            screen: 'intro',
            state: {asked: [], filled: {}},
            messages: [],
            personaData: this.loadPersonaData(),
            isLoading: false,
            adviceText: '',
            storyText: '',
            groups: [],
        };
    },

    watch: {
        personaKey() {
            this.personaData = this.loadPersonaData();
        },
    },

    created() {
        this.restoreResult();
    },

    methods: {
        // Show the result the visitor saw earlier, so leaving the page and coming
        // back does not throw the advice away.
        restoreResult() {
            const stored = loadResult(this.personaKey);
            if (!stored) {
                return;
            }
            this.adviceText = stored.adviceText;
            this.storyText = stored.storyText || '';
            this.groups = stored.groups;
            this.screen = 'result';
        },

        loadPersonaData() {
            const personas = (window.drupalSettings && window.drupalSettings.vnemlStoryfinder && window.drupalSettings.vnemlStoryfinder.personas) || [];
            const key = this.personaKey.toLowerCase();
            return personas.find(function (persona) {
                return persona.key === key;
            }) || {
                key,
                label: 'Jouw gids',
                tagline: '',
                imageUrl: '',
            };
        },

        startConversation(starterAnswer) {
            this.screen = 'chat';
            if (starterAnswer) {
                this.addMessage('user', starterAnswer);
            }
            this.fetchNextQuestion();
        },

        async handleAnswer({slot, value, label}) {
            this.state.filled[slot] = value;
            this.state.asked.push(slot);
            this.addMessage('user', label || String(value));
            await this.fetchNextQuestion();
        },

        async fetchNextQuestion() {
            this.isLoading = true;
            try {
                const resp = await this.post(this.endpointNext, {
                    persona: this.personaKey,
                    state: this.state,
                });
                if (resp.done) {
                    await this.fetchAdvice();
                } else {
                    this.addMessage('bot', null, resp.question);
                }
            } catch (e) {
                this.addMessage('bot', 'Er ging iets mis. Probeer het opnieuw.');
            } finally {
                this.isLoading = false;
            }
        },

        async fetchAdvice() {
            this.screen = 'loading';
            try {
                const resp = await this.post(this.endpointAdvice, {
                    persona: this.personaKey,
                    state: this.state,
                });
                this.adviceText = resp.advice_text || '';
                this.storyText = resp.story_text || '';
                this.groups = resp.groups || [];
                this.screen = 'result';
                saveResult(this.personaKey, this.adviceText, this.groups, this.storyText);
            } catch (e) {
                // A failed request is not stored: the visitor should be able to retry
                // instead of being sent back to an error message on every visit.
                this.adviceText = 'Ontdek Hart van Limburg op jouw manier.';
                this.storyText = 'We gaan samen Hart van Limburg ontdekken!';
                this.groups = [];
                this.screen = 'result';
            }
        },

        addMessage(type, text, question) {
            this.messages.push({type, text: text || null, question: question || null});
        },

        async post(url, body) {
            const resp = await fetch(url, {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': this.csrfToken,
                },
                body: JSON.stringify(body),
            });
            if (!resp.ok) {
                throw new Error('HTTP ' + resp.status);
            }
            return resp.json();
        },

        reset() {
            clearResult();
            this.screen = 'intro';
            this.state = {asked: [], filled: {}};
            this.messages = [];
            this.adviceText = '';
            this.storyText = '';
            this.groups = [];
        },
    },
};
</script>

<style lang="scss">
.sf-wizard {
    font-family: 'Comfortaa', sans-serif;
    min-height: 80vh;
    padding-bottom: 2rem;
    display: flex;
    flex-direction: column;
    transition: background-color 0.35s ease;

    &--intro {
        background-color: #517b86;
    }

    &--chat,
    &--loading,
    &--result {
        background-color: #517b86;
    }

    &__view {
        width: 100%;
        display: flex;
        flex-direction: column;
        flex: 1;

        &--chat {
            height: 100%;
        }
    }

    &__chat-container {
        display: flex;
        flex-direction: column;
        flex: 1;
        max-width: 600px;
        padding-left: 0;
        padding-right: 0;
    }
}

/* Screen transition animations */
.sf-screen-fade-enter-active,
.sf-screen-fade-leave-active {
    transition: opacity 0.3s cubic-bezier(0.25, 1, 0.5, 1), transform 0.3s cubic-bezier(0.25, 1, 0.5, 1);
}

.sf-screen-fade-enter {
    opacity: 0;
    transform: translateY(12px);
}

.sf-screen-fade-leave-to {
    opacity: 0;
    transform: translateY(-8px);
}
</style>
